/**
 * Ask ABHI — the chat-with-your-data assistant.
 *
 * A small "Ask ABHI" pill (stacked above the Feedback pill) opens a
 * right-hand panel. Rendered for any signed-in user the backend says may
 * use it (GET /api/chat/status); everyone else sees nothing.
 *
 * Trust is the product, so the UI keeps it visible without shouting:
 * numbered citation chips open the exact rows (or visit note) behind each
 * figure, a "Verified" mark shows the backend's check passed, and a
 * withheld answer says why.
 *
 * Shortcut: ⌘J / Ctrl+J toggles the panel.
 */

import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import LiquorOutlinedIcon from '@mui/icons-material/LiquorOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import UpdateIcon from '@mui/icons-material/Update';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryIcon from '@mui/icons-material/History';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import VerifiedIcon from '@mui/icons-material/Verified';
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import {
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ThemeProvider, createTheme, type Theme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';

import {
  ChatStreamError,
  sendChatFeedback,
  type ChatFeedbackReason,
  useArchiveConversation,
  useAskChat,
  useChatConversation,
  useChatConversations,
  useChatStatus,
  useChatSuggestions,
  type ChatCitation,
  type ChatLiveStep,
  type ChatMessage,
  type ChatSuggestion,
  type ChatSuggestionKind,
} from '../../api/chat';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import { ChatMarkdown } from './ChatMarkdown';
import {
  FONT_SANS,
  ACCENT,
  ACCENT_TINT,
  ACCENT_DEEP,
  ACCENT_SOFT,
  COMPACT_QUERY,
  INK,
  LINE,
  LINE_STRONG,
  LINK,
  MUTED,
  PANEL,
  PANEL_TINT,
  PANEL_Z,
  SUBTLE,
  SURFACE,
  TEXT,
  FS,
  TRACKING,
} from './chatTheme';
import { playChatSound, isChatMuted, setChatMuted } from './chat-sound';
import { SourceDialog } from './SourceDialog';

const MotionBox = motion.create(Box);
const QUESTION_MAX = 2000;

type Topic = {
  label: string;
  question: string;
  Icon: ComponentType<{ sx?: object }>;
  needs: 'depletions' | 'notes';
};

/** Short chips; each sends a full, well-formed question. */
const TOPICS: Topic[] = [
  {
    label: 'Top accounts',
    question: 'Top 10 accounts by 9L cases this year',
    Icon: EmojiEventsOutlinedIcon,
    needs: 'depletions',
  },
  {
    label: 'Year over year',
    question: 'How are depletions this year compared with the same months last year?',
    Icon: TrendingUpIcon,
    needs: 'depletions',
  },
  {
    label: 'By state',
    question: 'Which states have the most 9L cases this year?',
    Icon: MapOutlinedIcon,
    needs: 'depletions',
  },
  {
    label: 'Stopped ordering',
    question: "Which accounts haven't ordered in the last 3 months?",
    Icon: HourglassBottomIcon,
    needs: 'depletions',
  },
  {
    label: 'Premise mix',
    question: 'How does ON-premise compare to OFF-premise this year?',
    Icon: DonutLargeIcon,
    needs: 'depletions',
  },
  {
    label: 'Top products',
    question: 'Which products have the most 9L cases this year?',
    Icon: LiquorOutlinedIcon,
    needs: 'depletions',
  },
  {
    label: 'My territory',
    question: 'How is my territory trending this year vs last year?',
    Icon: PlaceOutlinedIcon,
    needs: 'depletions',
  },
  {
    label: 'Recent visit notes',
    question: 'Summarize my visit notes from the last 30 days',
    Icon: EventNoteOutlinedIcon,
    needs: 'notes',
  },
  {
    label: 'Data freshness',
    question: 'How current is the depletions data?',
    Icon: UpdateIcon,
    needs: 'depletions',
  },
];

function stripCitations(text: string): string {
  return text.replace(/\s*\[(?:[SN]\d+)(?:\s*,\s*[SN]\d+)*\]/g, '');
}

/** "2026-10-31" → "Oct 31, 2026". */
function formatShortDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const eyebrow = {
  fontSize: FS.micro,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: SUBTLE,
} as const;

// ---------------------------------------------------------------------
// Launcher
// ---------------------------------------------------------------------
export function AskHy() {
  const { user, isAuthenticated } = useAuth();
  const status = useChatStatus(isAuthenticated);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!isAuthenticated || !user || !status.data?.enabled) return null;

  return (
    <ThemeProvider theme={withLightTooltips}>
      <AnimatePresence>{!open && <Launcher onOpen={() => setOpen(true)} />}</AnimatePresence>
      <AskHyPanel open={open} onClose={() => setOpen(false)} firstName={user.first_name} />
    </ThemeProvider>
  );
}

/**
 * Ask ABHI's tooltips: light card style matching the panel (white, hairline
 * border, soft shadow) instead of MUI's default near-black. Scoped to this
 * component tree so the rest of the dashboard is unchanged.
 */
function withLightTooltips(outer: Theme): Theme {
  return createTheme(outer, {
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: PANEL,
            color: TEXT,
            fontFamily: FONT_SANS,
            fontSize: FS.label,
            fontWeight: 500,
            lineHeight: 1.45,
            letterSpacing: TRACKING,
            padding: '6px 10px',
            borderRadius: 8,
            border: `1px solid ${LINE}`,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.10), 0 1px 3px rgba(0, 0, 0, 0.06)',
            maxWidth: 280,
          },
          arrow: {
            color: PANEL,
            '&::before': { border: `1px solid ${LINE}` },
          },
        },
      },
    },
  });
}

// ---------------------------------------------------------------------
// Launcher button
// ---------------------------------------------------------------------
/** Times the label reveals itself per page load before settling. */
const PEEK_COUNT = 2;
const PEEK_FIRST_MS = 1600;
const PEEK_HOLD_MS = 3200;
const PEEK_GAP_MS = 12000;

const LABEL = 'Ask ABHI';
/**
 * 56px — the size Intercom, Zendesk and Drift launchers use. Collapsed it
 * is a 56px circle. Logo 40px, leaving an 8px white margin so the petals
 * never crowd the edge.
 */
const HEIGHT = 56;
const LOGO = 40;
/** 1.5px gradient ring around the circle (inside HEIGHT). */
const RING = 1.5;
const PAD = (HEIGHT - LOGO) / 2 - RING;
const RING_GRADIENT = 'linear-gradient(135deg, #b581f9 0%, #7c20f4 50%, #144492 100%)';
/** Apple-style ease-out: quick start, long soft landing, no overshoot. */
const EASE_OUT = [0.32, 0.72, 0, 1] as const;
const ENTER_DELAY_S = 0.5;

const labelVariants = {
  hidden: { transition: { staggerChildren: 0.015, staggerDirection: -1 } },
  shown: { transition: { staggerChildren: 0.03, delayChildren: 0.1 } },
};
const letterVariants = {
  hidden: { opacity: 0, y: 4, filter: 'blur(3px)', transition: { duration: 0.14 } },
  shown: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.28, ease: EASE_OUT } },
};

/**
 * Slim ABHI launcher.
 *
 * A 56px white circle with the logo and a slowly turning purple aura. On
 * page load it springs in with a soft two-note chime; until the person
 * opens Ask ABHI, two rings ripple out every few seconds to draw the eye.
 * The label slides out a couple of times after load and on hover / focus.
 * After the first open it comes back calm: aura only, no ripples or peeks.
 * Reduced motion: a still aura, no ripples or peeking, a plain fade in.
 */
/** True once the launcher has made its entrance in this page load. */
let arrived = false;
/** True once Ask ABHI has been opened in this page load. */
let engaged = false;
/** Pause between attention ripples. */
const RIPPLE_EVERY_S = 4.5;
const AURA = 'conic-gradient(from 0deg, #7c20f4, #b581f9, #144492, #a769f7, #7c20f4)';

function Launcher({ onOpen }: { onOpen: () => void }) {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [peek, setPeek] = useState(false);
  const [hover, setHover] = useState(false);
  const [labelWidth, setLabelWidth] = useState(0);
  const measureRef = useRef<HTMLSpanElement>(null);
  const expanded = hover || peek;
  // Captured on first render: later mounts (after closing the panel) are quiet.
  const [entrance] = useState(() => !arrived);
  const [attention] = useState(() => !engaged);
  const open = () => {
    engaged = true;
    onOpen();
  };

  // Measure the label once fonts are ready so the pill opens to its exact width.
  useEffect(() => {
    const measure = () => setLabelWidth(measureRef.current?.offsetWidth ?? 0);
    measure();
    void document.fonts?.ready.then(measure);
  }, []);

  useEffect(() => {
    if (!entrance) return;
    arrived = true;
    const t = setTimeout(() => playChatSound('hello'), ENTER_DELAY_S * 1000 + 120);
    return () => clearTimeout(t);
  }, [entrance]);

  useEffect(() => {
    if (reduceMotion || !entrance) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 0; i < PEEK_COUNT; i += 1) {
      const start = PEEK_FIRST_MS + i * (PEEK_HOLD_MS + PEEK_GAP_MS);
      timers.push(setTimeout(() => setPeek(true), start));
      timers.push(setTimeout(() => setPeek(false), start + PEEK_HOLD_MS));
    }
    return () => timers.forEach(clearTimeout);
  }, [reduceMotion, entrance]);

  const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    // The logo's own artwork already leaves air on its right; keep the gap tight.
    pl: 0.75,
    pr: 1.25,
    fontSize: FS.body,
    fontWeight: 600,
    letterSpacing: TRACKING,
    whiteSpace: 'nowrap',
  } as const;

  return (
    <MotionBox
      key="launcher"
      initial={reduceMotion || !entrance ? { opacity: 0 } : { opacity: 0, scale: 0.5, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8, y: 6, transition: { duration: 0.18 } }}
      transition={
        reduceMotion || !entrance
          ? { duration: 0.2 }
          : { type: 'spring', stiffness: 420, damping: 24, delay: ENTER_DELAY_S }
      }
      sx={{
        position: 'fixed',
        // The Feedback tab moved to the right edge, so the corner is the launcher's own.
        bottom: {
          xs: 'calc(16px + env(safe-area-inset-bottom))',
          sm: 'calc(24px + env(safe-area-inset-bottom))',
        },
        right: {
          xs: 'calc(16px + env(safe-area-inset-right))',
          sm: 'calc(24px + env(safe-area-inset-right))',
        },
        zIndex: 1250,
      }}
    >
      {/* Off-screen copy of the label, for measuring its width. */}
      <Box
        component="span"
        ref={measureRef}
        aria-hidden
        sx={{ ...labelStyle, position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }}
      >
        {LABEL}
      </Box>

      {/* Attention: two rings ripple out from the logo, every few seconds. */}
      {!reduceMotion &&
        attention &&
        [0, 1].map((i) => (
          <MotionBox
            key={i}
            aria-hidden
            initial={{ opacity: 0, scale: 1 }}
            animate={{ opacity: [0, 0.28, 0], scale: [1, 1.5] }}
            transition={{
              duration: 1.6,
              ease: 'easeOut',
              delay: ENTER_DELAY_S + 0.15 + i * 0.4,
              repeat: Infinity,
              repeatDelay: RIPPLE_EVERY_S,
            }}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: HEIGHT,
              height: HEIGHT,
              borderRadius: '50%',
              border: `1px solid ${ACCENT}`,
              pointerEvents: 'none',
            }}
          />
        ))}

      <Box
        component="button"
        type="button"
        onClick={open}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        aria-label="Open Ask ABHI assistant (⌘J)"
        sx={{
          all: 'unset',
          cursor: 'pointer',
          position: 'relative',
          display: 'block',
          borderRadius: 999,
          fontFamily: FONT_SANS,
          color: INK,
          transition: 'transform 200ms cubic-bezier(0.32, 0.72, 0, 1)',
          '&:hover': { transform: 'translateY(-1px)' },
          '&:active': { transform: 'scale(0.97)' },
          '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 3 },
          '&:hover .abhi-pill': {
            boxShadow: `0 8px 24px rgba(124, 32, 244, 0.18), 0 1px 2px rgba(24, 24, 27, 0.06)`,
          },
        }}
      >
        {/* Aura: a blurred purple gradient turning slowly behind the pill. */}
        <MotionBox
          aria-hidden
          animate={reduceMotion ? undefined : { opacity: [0.55, 0.9, 0.55] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
          sx={{
            position: 'absolute',
            inset: -3,
            borderRadius: 999,
            filter: 'blur(7px)',
            opacity: attention ? 0.7 : 0.45,
            pointerEvents: 'none',
          }}
        >
          <Box sx={{ position: 'absolute', inset: 0, borderRadius: 999, overflow: 'hidden' }}>
            <MotionBox
              animate={reduceMotion ? undefined : { rotate: 360 }}
              transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 320,
                height: 320,
                marginTop: '-160px',
                marginLeft: '-160px',
                background: AURA,
              }}
            />
          </Box>
        </MotionBox>

        {/* The pill itself — width animates to the measured label width. */}
        <MotionBox
          className="abhi-pill"
          initial={false}
          animate={{ width: HEIGHT + (expanded ? labelWidth : 0) }}
          transition={{ duration: 0.5, ease: EASE_OUT }}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            height: HEIGHT,
            p: `${PAD}px`,
            boxSizing: 'border-box',
            borderRadius: 999,
            // White fill inside, purple gradient showing through a transparent border.
            border: `${RING}px solid transparent`,
            background: `linear-gradient(#fff, #fff) padding-box, ${RING_GRADIENT} border-box`,
            boxShadow: `0 6px 18px rgba(24, 24, 27, 0.10), 0 1px 2px rgba(24, 24, 27, 0.06)`,
            transition: 'box-shadow 200ms ease',
          }}
        >
          <AbhiLogo size={LOGO} />
          <MotionBox
            aria-hidden
            initial={false}
            variants={labelVariants}
            animate={expanded ? 'shown' : 'hidden'}
            sx={labelStyle}
          >
            <Box component="span" sx={{ display: 'flex' }}>
              {LABEL.split('').map((ch, i) => (
                <MotionBox
                  key={i}
                  variants={letterVariants}
                  sx={{ display: 'inline-block', whiteSpace: 'pre' }}
                >
                  {ch}
                </MotionBox>
              ))}
            </Box>
          </MotionBox>
        </MotionBox>
      </Box>
    </MotionBox>
  );
}

// ---------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------
function AskHyPanel({
  open,
  onClose,
  firstName,
}: {
  open: boolean;
  onClose: () => void;
  firstName: string;
}) {
  // Phones, and phones in landscape (too short for a floating window): full screen.
  const isMobile = useMediaQuery(COMPACT_QUERY);
  const status = useChatStatus(true);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [muted, setMuted] = useState(isChatMuted);
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const [liveSteps, setLiveSteps] = useState<ChatLiveStep[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeSource, setActiveSource] = useState<{ source: ChatCitation; n: number } | null>(
    null,
  );
  const conversation = useChatConversation(conversationId);
  const ask = useAskChat();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const messages = useMemo(() => conversation.data?.messages ?? [], [conversation.data]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // A new answer: bring its first line into view rather than its last,
    // so a long answer is read from the top. Otherwise (question sent,
    // progress arriving) follow the bottom.
    const last = messages[messages.length - 1];
    if (!pending && last?.role === 'assistant') {
      const node = el.querySelector<HTMLElement>(`[data-msg="${last.id}"]`);
      if (node) {
        el.scrollTo({ top: node.offsetTop - 16, behavior: 'smooth' });
        return;
      }
    }
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, pending]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('[role="presentation"].MuiDialog-root')) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open && !showHistory) setTimeout(() => inputRef.current?.focus(), 250);
  }, [open, showHistory, conversationId]);

  const submit = useCallback(
    (text: string) => {
      const content = text.trim();
      if (!content || ask.isPending) return;
      setError(null);
      setPending(content);
      setLiveSteps([]);
      setDraft('');
      ask.mutate(
        {
          conversation_id: conversationId,
          content,
          onStep: (step) =>
            setLiveSteps((prev) => {
              const i = prev.findIndex((p) => p.key === step.key);
              if (i === -1) return [...prev, step];
              const next = prev.slice();
              next[i] = step;
              return next;
            }),
        },
        {
          onSuccess: (res) => {
            setConversationId(res.conversation.id);
            playChatSound('ready');
          },
          onError: (e) => {
            setDraft(content);
            // Stay in the thread the question was saved to, so a retry continues it.
            if (e instanceof ChatStreamError && e.conversationId !== null) {
              setConversationId(e.conversationId);
            }
            setError(
              e instanceof ApiError
                ? e.serverMessage
                : e instanceof ChatStreamError
                  ? e.message
                  : 'Something went wrong. Please try again.',
            );
          },
          onSettled: () => setPending(null),
        },
      );
    },
    [ask, conversationId],
  );

  const newChat = () => {
    setConversationId(null);
    setShowHistory(false);
    setError(null);
    setDraft('');
  };

  const remaining = status.data ? status.data.daily_limit - status.data.used_today : null;
  const empty = messages.length === 0 && !pending;

  return (
    <MotionBox
      role="dialog"
      aria-label="Ask ABHI assistant"
      aria-hidden={!open}
      initial={false}
      animate={
        open
          ? { opacity: 1, scale: 1, y: 0, visibility: 'visible' }
          : { opacity: 0, scale: 0.92, y: 24, transitionEnd: { visibility: 'hidden' } }
      }
      transition={{ duration: 0.38, ease: [0.32, 0.72, 0, 1] }}
      sx={{
        position: 'fixed',
        zIndex: PANEL_Z,
        // Grows out of the launcher in the bottom-right corner.
        transformOrigin: 'bottom right',
        right: isMobile ? 0 : 24,
        bottom: isMobile ? 0 : 24,
        // Never wider or taller than the viewport minus a 24px margin (tablets,
        // small laptops). dvh follows mobile browser toolbars; vh is the fallback.
        width: isMobile ? '100%' : `min(${expanded ? 760 : 420}px, calc(100vw - 48px))`,
        height: isMobile
          ? ['100vh', '100dvh']
          : expanded
            ? ['min(860px, calc(100vh - 48px))', 'min(860px, calc(100dvh - 48px))']
            : ['min(680px, calc(100vh - 48px))', 'min(680px, calc(100dvh - 48px))'],
        // Clear the notch and home indicator when full screen.
        ...(isMobile && {
          pt: 'env(safe-area-inset-top)',
          pb: 'env(safe-area-inset-bottom)',
          pl: 'env(safe-area-inset-left)',
          pr: 'env(safe-area-inset-right)',
        }),
        transition:
          'width 320ms cubic-bezier(0.32, 0.72, 0, 1), height 320ms cubic-bezier(0.32, 0.72, 0, 1)',
        borderRadius: isMobile ? 0 : '20px',
        overflow: 'hidden',
        bgcolor: PANEL_TINT,
        fontFamily: FONT_SANS,
        letterSpacing: TRACKING,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        '& .MuiTypography-root': { letterSpacing: 'inherit' },
        border: isMobile ? 'none' : `1px solid ${LINE}`,
        boxShadow:
          '0 32px 80px -12px rgba(0, 0, 0, 0.28), 0 8px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(255,255,255,0.6) inset',
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          bgcolor: PANEL,
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        <AbhiLogo size={32} />
        <Typography sx={{ fontWeight: 650, fontSize: FS.body, color: INK }}>Ask ABHI</Typography>
        <Box
          sx={{
            px: 0.6,
            py: 0.1,
            borderRadius: '5px',
            border: `1px solid ${LINE_STRONG}`,
            fontSize: '0.625rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            color: MUTED,
          }}
        >
          BETA
        </Box>
        <Box sx={{ flex: 1 }} />
        <HeaderButton
          title={
            ask.isPending
              ? 'Available when the answer is ready'
              : showHistory
                ? 'Back to chat'
                : 'History'
          }
          onClick={() => setShowHistory((s) => !s)}
          active={showHistory}
          disabled={ask.isPending}
        >
          <HistoryIcon sx={{ fontSize: 19 }} />
        </HeaderButton>
        <HeaderButton
          title={ask.isPending ? 'Available when the answer is ready' : 'New chat'}
          onClick={newChat}
          disabled={ask.isPending}
        >
          <AddIcon sx={{ fontSize: 20 }} />
        </HeaderButton>
        {!isMobile && (
          <HeaderButton
            title={expanded ? 'Collapse' : 'Expand'}
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? (
              <CloseFullscreenIcon sx={{ fontSize: 17 }} />
            ) : (
              <OpenInFullIcon sx={{ fontSize: 17 }} />
            )}
          </HeaderButton>
        )}
        <HeaderButton title="Close" onClick={onClose}>
          <CloseIcon sx={{ fontSize: 19 }} />
        </HeaderButton>
      </Box>

      {showHistory ? (
        <HistoryList
          activeId={conversationId}
          onPick={(id) => {
            setConversationId(id);
            setShowHistory(false);
            setError(null);
          }}
          onDeleted={(id) => {
            if (id === conversationId) setConversationId(null);
          }}
          onNew={newChat}
          onBack={() => setShowHistory(false)}
        />
      ) : (
        <>
          {/* Thread */}
          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              position: 'relative',
              px: 2.5,
              py: 3,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {empty ? (
              <EmptyState
                firstName={firstName}
                canDepletions={status.data?.can_depletions ?? false}
                canNotes={status.data?.can_notes ?? false}
                onPick={submit}
                loading={conversationId !== null && conversation.isLoading}
                open={open}
                dataThrough={status.data?.data_through ?? null}
                territories={status.data?.territories ?? []}
              />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {messages.map((m) =>
                  m.role === 'user' ? (
                    <UserBubble key={m.id} text={m.content} />
                  ) : (
                    <AssistantMessage
                      key={m.id}
                      message={m}
                      onCite={(source, n) => setActiveSource({ source, n })}
                    />
                  ),
                )}
                {!pending && <FollowUps message={messages[messages.length - 1]} onPick={submit} />}
                {pending && (
                  <>
                    <UserBubble text={pending} />
                    <LiveAnswer steps={liveSteps} />
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* Composer — floats on the thread background; the thread fades out under it. */}
          <Box
            sx={{
              position: 'relative',
              px: 2,
              pb: 1.25,
              pt: 0.5,
              flexShrink: 0,
              bgcolor: PANEL_TINT,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                top: -20,
                height: 20,
                background: `linear-gradient(to bottom, transparent, ${PANEL_TINT})`,
                pointerEvents: 'none',
              },
            }}
          >
            {error && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  alignItems: 'center',
                  mb: 1,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  bgcolor: '#fef2f2',
                  color: '#b91c1c',
                  fontSize: FS.table,
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 16 }} />
                {error}
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 1,
                p: 0.75,
                pl: 1.75,
                borderRadius: '16px',
                bgcolor: PANEL,
                border: `1px solid ${LINE_STRONG}`,
                boxShadow: '0 1px 2px rgba(24, 24, 27, 0.04), 0 6px 20px rgba(24, 24, 27, 0.05)',
                transition: 'border-color 150ms ease, box-shadow 150ms ease',
                '&:focus-within': {
                  borderColor: 'rgba(124, 32, 244, 0.45)',
                  boxShadow: `0 0 0 3px ${ACCENT_SOFT}, 0 6px 20px rgba(24, 24, 27, 0.05)`,
                },
              }}
            >
              <InputBase
                inputRef={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value.slice(0, QUESTION_MAX))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submit(draft);
                  }
                }}
                placeholder="Ask about accounts, products or states…"
                multiline
                maxRows={6}
                disabled={ask.isPending}
                sx={{
                  flex: 1,
                  py: 0.75,
                  fontSize: FS.body,
                  fontFamily: FONT_SANS,
                  color: TEXT,
                  '& textarea::placeholder': { color: SUBTLE, opacity: 1 },
                }}
                inputProps={{ 'aria-label': 'Ask ABHI a question' }}
              />
              {draft.length > QUESTION_MAX * 0.8 && (
                <Typography
                  sx={{
                    alignSelf: 'center',
                    fontSize: FS.micro,
                    fontVariantNumeric: 'tabular-nums',
                    color: draft.length >= QUESTION_MAX ? '#b91c1c' : SUBTLE,
                  }}
                >
                  {draft.length.toLocaleString()}/{QUESTION_MAX.toLocaleString()}
                </Typography>
              )}
              <IconButton
                onClick={() => submit(draft)}
                disabled={!draft.trim() || ask.isPending}
                aria-label="Send"
                sx={{
                  width: 34,
                  height: 34,
                  flexShrink: 0,
                  borderRadius: '11px',
                  color: '#fff',
                  background: 'linear-gradient(135deg, #8a38f5 0%, #6a17d6 100%)',
                  boxShadow: '0 2px 6px rgba(124, 32, 244, 0.35)',
                  transition: 'transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease',
                  '&:hover': { boxShadow: '0 4px 12px rgba(124, 32, 244, 0.4)' },
                  '&:active': { transform: 'scale(0.94)' },
                  '&.Mui-disabled': { background: SURFACE, color: SUBTLE, boxShadow: 'none' },
                }}
              >
                {ask.isPending ? (
                  <CircularProgress size={14} thickness={5} sx={{ color: SUBTLE }} />
                ) : (
                  <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Box>
            <Box
              sx={{
                mt: 0.875,
                position: 'relative',
                px: 4,
                textAlign: 'center',
                fontSize: FS.micro,
                lineHeight: '22px',
                color: SUBTLE,
              }}
            >
              Ask ABHI can make mistakes. Check important numbers.
              {remaining !== null && remaining <= 20 && ` · ${remaining} questions left today`}
              <Tooltip title={muted ? 'Turn sounds on' : 'Turn sounds off'}>
                <IconButton
                  size="small"
                  aria-label={muted ? 'Turn sounds on' : 'Turn sounds off'}
                  onClick={() => {
                    setChatMuted(!muted);
                    setMuted(!muted);
                  }}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    width: 22,
                    height: 22,
                    color: SUBTLE,
                    '&:hover': { color: INK, bgcolor: SURFACE },
                  }}
                >
                  {muted ? (
                    <VolumeOffOutlinedIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <VolumeUpOutlinedIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </>
      )}

      <SourceDialog
        source={activeSource?.source ?? null}
        number={activeSource?.n ?? null}
        onClose={() => setActiveSource(null)}
      />
    </MotionBox>
  );
}

// ---------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------
/**
 * The ABHI mark from the ask-abhi brand kit, cropped square around the
 * flower so it centres in a circle (the smallest trailing dot is cropped).
 */
const ABHI_LOGO_SRC = '/ask-abhi/logo-mark.svg';

function AbhiLogo({ size }: { size: number }) {
  return (
    <Box
      component="img"
      src={ABHI_LOGO_SRC}
      alt=""
      aria-hidden
      draggable={false}
      sx={{ width: size, height: size, flexShrink: 0, display: 'block', userSelect: 'none' }}
    />
  );
}

function Mark({ size }: { size: number }) {
  return <AbhiLogo size={size} />;
}

function HeaderButton({
  title,
  onClick,
  active,
  disabled,
  children,
}: {
  title: string;
  onClick: () => void;
  active?: boolean;
  /** While an answer is in flight, switching threads would strand it. */
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          onClick={onClick}
          disabled={disabled}
          aria-label={title}
          size="small"
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            color: active ? INK : MUTED,
            bgcolor: active ? SURFACE : 'transparent',
            '&:hover': { color: INK, bgcolor: SURFACE },
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function EmptyState({
  firstName,
  canDepletions,
  canNotes,
  onPick,
  loading,
  open,
  dataThrough,
  territories,
}: {
  firstName: string;
  canDepletions: boolean;
  canNotes: boolean;
  onPick: (q: string) => void;
  loading: boolean;
  open: boolean;
  dataThrough: string | null;
  territories: string[];
}) {
  const insights = useChatSuggestions(open);
  if (loading) return null;
  const topics = TOPICS.filter((t) => (t.needs === 'notes' ? canNotes : canDepletions));
  const cards = insights.data ?? [];
  return (
    <MotionBox
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <Typography
        sx={{
          fontSize: '1.25rem',
          fontWeight: 650,
          letterSpacing: '-0.02em',
          lineHeight: 1.3,
          color: INK,
        }}
      >
        What would you like to know, {firstName}?
      </Typography>
      <Tooltip
        placement="bottom-start"
        title={`Answers come only from Hooten Young depletions${
          canNotes ? ' and visit notes' : ''
        }. Every figure is checked against the data and linked to its source. The latest month is usually partial.`}
      >
        <Box
          sx={{
            mt: 0.75,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 1,
            rowGap: 0.25,
            fontSize: FS.label,
            color: MUTED,
            cursor: 'default',
            '& > .sep': { color: SUBTLE },
          }}
        >
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <VerifiedIcon sx={{ fontSize: 13, color: ACCENT }} />
            Verified answers
          </Box>
          {dataThrough && (
            <>
              <span className="sep">·</span>
              <span>Data through {formatShortDate(dataThrough)}</span>
            </>
          )}
          <span className="sep">·</span>
          <span>{territories.length ? territories.join(', ') : 'All states'}</span>
        </Box>
      </Tooltip>

      {(insights.isLoading || cards.length > 0) && (
        <>
          <Typography sx={{ ...eyebrow, mt: 3, mb: 1 }}>Worth a look right now</Typography>
          <Box
            sx={{
              borderRadius: '12px',
              bgcolor: PANEL,
              border: `1px solid ${LINE}`,
              overflow: 'hidden',
              '& > * + *': { borderTop: `1px solid ${LINE}` },
            }}
          >
            {insights.isLoading
              ? [0, 1, 2].map((i) => <InsightSkeleton key={i} />)
              : cards.map((c, i) => (
                  <InsightRow key={c.question} item={c} index={i} onPick={onPick} />
                ))}
          </Box>
        </>
      )}

      <Typography sx={{ ...eyebrow, mt: 3, mb: 1 }}>
        {cards.length ? 'Or ask about' : 'A few places to start'}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {topics.map(({ label, question, Icon }, i) => (
          <MotionBox
            key={label}
            role="button"
            tabIndex={0}
            title={question}
            onClick={() => onPick(question)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onPick(question);
              }
            }}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.03 * i, duration: 0.2 }}
            sx={{
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              height: 30,
              px: 1.25,
              borderRadius: 999,
              bgcolor: PANEL,
              border: `1px solid ${LINE}`,
              fontSize: FS.label,
              fontWeight: 500,
              color: TEXT,
              transition: 'all 120ms ease',
              '&:hover, &:focus-visible': {
                borderColor: LINE_STRONG,
                color: INK,
                bgcolor: SURFACE,
                outline: 'none',
              },
              '&:hover .topic-icon, &:focus-visible .topic-icon': { color: ACCENT },
            }}
          >
            <Box
              component="span"
              className="topic-icon"
              sx={{ display: 'inline-flex', color: MUTED, transition: 'color 120ms ease' }}
            >
              <Icon sx={{ fontSize: 14 }} />
            </Box>
            {label}
          </MotionBox>
        ))}
      </Box>
    </MotionBox>
  );
}

const INSIGHT_ICON: Record<ChatSuggestionKind, ComponentType<{ sx?: object }>> = {
  growth: TrendingUpIcon,
  decline: TrendingDownIcon,
  risk: HourglassBottomIcon,
  new: PersonAddAltOutlinedIcon,
  product: LiquorOutlinedIcon,
  channel: DonutLargeIcon,
  notes: EventNoteOutlinedIcon,
};

const TONE: Record<ChatSuggestion['tone'], { fg: string; bg: string }> = {
  up: { fg: '#047857', bg: '#ecfdf5' },
  down: { fg: '#b91c1c', bg: '#fef2f2' },
  warn: { fg: '#b45309', bg: '#fffbeb' },
  info: { fg: '#1d4ed8', bg: '#eff6ff' },
};

/** One row: tinted icon · question with its fact underneath · metric tag. */
function InsightRow({
  item,
  index,
  onPick,
}: {
  item: ChatSuggestion;
  index: number;
  onPick: (q: string) => void;
}) {
  const Icon = INSIGHT_ICON[item.kind];
  const tone = TONE[item.tone];
  return (
    <Tooltip title={item.fact} placement="left" enterDelay={400} arrow>
      <MotionBox
        role="button"
        tabIndex={0}
        aria-label={`${item.question} (${item.fact})`}
        onClick={() => onPick(item.question)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onPick(item.question);
          }
        }}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, delay: 0.04 * index, ease: [0.32, 0.72, 0, 1] }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minHeight: 56,
          px: 1.5,
          py: 1,
          cursor: 'pointer',
          transition: 'background-color 120ms ease',
          '&:hover, &:focus-visible': { bgcolor: PANEL_TINT, outline: 'none' },
          '&:hover .insight-go, &:focus-visible .insight-go': {
            color: INK,
            transform: 'translateX(2px)',
          },
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: tone.bg,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 16, color: tone.fg }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontSize: FS.sm, fontWeight: 500, color: INK, lineHeight: 1.35 }}
          >
            {item.label}
          </Typography>
          <Typography noWrap sx={{ fontSize: FS.label, color: MUTED, lineHeight: 1.4 }}>
            {item.fact}
          </Typography>
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            px: 0.85,
            height: 22,
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: 999,
            fontSize: FS.micro,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            color: tone.fg,
            bgcolor: tone.bg,
            whiteSpace: 'nowrap',
          }}
        >
          {item.metric}
        </Box>
        <ChevronRightIcon
          className="insight-go"
          sx={{
            fontSize: 18,
            color: SUBTLE,
            flexShrink: 0,
            ml: -0.5,
            mr: -0.75,
            transition: 'transform 150ms ease, color 150ms ease',
          }}
        />
      </MotionBox>
    </Tooltip>
  );
}

function InsightSkeleton() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: 56, px: 1.5 }}>
      <MotionBox
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 1.4, repeat: Infinity }}
        sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: SURFACE }}
      />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <MotionBox
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.4, repeat: Infinity, delay: 0.1 }}
          sx={{ height: 10, width: '55%', borderRadius: 1, bgcolor: SURFACE }}
        />
        <MotionBox
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.4, repeat: Infinity, delay: 0.15 }}
          sx={{ height: 8, width: '75%', borderRadius: 1, bgcolor: SURFACE }}
        />
      </Box>
      <MotionBox
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 1.4, repeat: Infinity, delay: 0.2 }}
        sx={{ width: 44, height: 18, borderRadius: 999, bgcolor: SURFACE }}
      />
    </Box>
  );
}

function UserBubble({ text }: { text: string }) {
  return (
    <MotionBox
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      sx={{ alignSelf: 'flex-end', maxWidth: '85%' }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: 2.5,
          bgcolor: SURFACE,
          color: INK,
          fontSize: FS.sm,
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {text}
      </Box>
    </MotionBox>
  );
}

function AssistantHeader({ thinking = false }: { thinking?: boolean }) {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{ position: 'relative', width: 22, height: 22, display: 'grid', placeItems: 'center' }}
      >
        {/* Thinking: a thin light orbits the (still) logo. */}
        {thinking && (
          <MotionBox
            aria-hidden
            animate={reduceMotion ? undefined : { rotate: 360 }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'linear' }}
            sx={{
              position: 'absolute',
              inset: -4,
              borderRadius: '50%',
              background: `conic-gradient(from 0deg, transparent 0deg, ${ACCENT} 90deg, transparent 180deg)`,
              WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 1.5px), #000 0)',
              mask: 'radial-gradient(farthest-side, transparent calc(100% - 1.5px), #000 0)',
            }}
          />
        )}
        <Mark size={22} />
      </Box>
      <Typography sx={{ fontSize: FS.sm, fontWeight: 600, color: INK }}>Ask ABHI</Typography>
    </Box>
  );
}

type StepView = {
  key: string;
  label: string;
  detail: string | null;
  state: 'active' | 'done' | 'error';
};

/** Text with a light sweeping across it — the "working on it" cue modern assistants use. */
const shimmerText = {
  background: `linear-gradient(90deg, ${MUTED} 0%, ${MUTED} 35%, ${INK} 50%, ${MUTED} 65%, ${MUTED} 100%)`,
  backgroundSize: '250% 100%',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  animation: 'abhiShimmer 1.8s linear infinite',
  '@keyframes abhiShimmer': {
    from: { backgroundPosition: '100% 0' },
    to: { backgroundPosition: '-150% 0' },
  },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none', color: INK, background: 'none' },
} as const;

/**
 * Steps as streamed can repeat ("Reviewing the results" after each lookup).
 * Show each label once, at its latest state, in first-seen order.
 */
function uniqueSteps(steps: StepView[]): StepView[] {
  const byLabel = new Map<string, StepView>();
  for (const st of steps) {
    const prev = byLabel.get(st.label);
    byLabel.set(st.label, prev ? { ...st, key: prev.key, detail: st.detail ?? prev.detail } : st);
  }
  return [...byLabel.values()];
}

/**
 * The step list — borderless, indented under the "Thinking" line, like the
 * reasoning trace in ChatGPT, Claude and Gemini. Shared by the live view and
 * the finished answer's "Thought for…" toggle.
 */
function StepList({ steps }: { steps: StepView[] }) {
  return (
    <Box
      role="list"
      sx={{
        position: 'relative',
        pl: 2,
        ml: '6px',
        borderLeft: `1px solid ${LINE_STRONG}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      {steps.map((st) => (
        <MotionBox
          role="listitem"
          key={st.key}
          initial={{ opacity: 0, y: 3 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: EASE_OUT }}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 0.75,
            rowGap: 0.25,
            minHeight: 20,
          }}
        >
          {st.state === 'active' ? (
            <MotionBox
              aria-hidden
              animate={{ opacity: [1, 0.35, 1] }}
              transition={{ duration: 1.2, repeat: Infinity }}
              sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: ACCENT, flexShrink: 0 }}
            />
          ) : st.state === 'error' ? (
            <PriorityHighIcon sx={{ fontSize: 13, color: '#b45309' }} />
          ) : (
            <CheckIcon sx={{ fontSize: 13, color: SUBTLE }} />
          )}
          <Typography
            component="span"
            sx={{
              fontSize: FS.table,
              lineHeight: 1.5,
              color: st.state === 'active' ? INK : MUTED,
              ...(st.state === 'active' && shimmerText),
            }}
          >
            {st.label}
          </Typography>
          {st.detail && (
            <Typography
              component="span"
              sx={{ fontSize: FS.label, color: SUBTLE, fontVariantNumeric: 'tabular-nums' }}
            >
              {st.detail}
            </Typography>
          )}
        </MotionBox>
      ))}
    </Box>
  );
}

/** The "Thought for 25s ›" / "Comparing two periods… 11s ›" line, plain text with a chevron. */
function TraceToggle({
  open,
  onToggle,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      sx={{
        all: 'unset',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        maxWidth: '100%',
        fontSize: FS.sm,
        color: MUTED,
        borderRadius: '6px',
        '&:hover, &:focus-visible': { color: INK },
        '&:hover .trace-chev, &:focus-visible .trace-chev': { color: INK },
        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
      }}
    >
      {children}
      <ChevronRightIcon
        className="trace-chev"
        sx={{
          fontSize: 17,
          color: SUBTLE,
          flexShrink: 0,
          transition: 'transform 150ms ease',
          transform: open ? 'rotate(90deg)' : 'none',
        }}
      />
    </Box>
  );
}

/**
 * What the user watches while the server works — real steps, streamed.
 *
 * No card, no border: one shimmering line naming the current step with a
 * timer, and a chevron to see every step (the way ChatGPT, Claude and
 * Gemini show their thinking). The trace stays open if the person opens it.
 */
function LiveAnswer({ steps }: { steps: ChatLiveStep[] }) {
  const [seconds, setSeconds] = useState(0);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setSeconds((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const shown = uniqueSteps(
    steps.length
      ? steps
      : [{ key: 'start', label: 'Understanding your question', detail: null, state: 'active' }],
  );
  const current =
    [...shown].reverse().find((st) => st.state === 'active') ?? shown[shown.length - 1];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <AssistantHeader thinking />
      <TraceToggle open={open} onToggle={() => setOpen((o) => !o)}>
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            ...shimmerText,
          }}
        >
          {current?.label ?? 'Thinking'}
        </Box>
        <Box
          component="span"
          sx={{ color: SUBTLE, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
        >
          {seconds}s
        </Box>
      </TraceToggle>
      <AnimatePresence initial={false}>
        {open && (
          <MotionBox
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: EASE_OUT }}
            sx={{ overflow: 'hidden' }}
          >
            <StepList steps={shown} />
          </MotionBox>
        )}
      </AnimatePresence>
      {/* Where the answer will appear: two faint shimmering lines. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }} aria-hidden>
        {['92%', '64%'].map((w, i) => (
          <MotionBox
            key={w}
            animate={{ opacity: [0.45, 0.9, 0.45] }}
            transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.2 }}
            sx={{
              height: 10,
              width: w,
              borderRadius: 999,
              background: `linear-gradient(90deg, ${SURFACE}, ${ACCENT_TINT}, ${SURFACE})`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

/** "Thought for 25s ›" — expands to the steps behind a finished answer. */
function ThoughtToggle({ message }: { message: ChatMessage }) {
  const [open, setOpen] = useState(false);
  if (!message.steps.length) return null;
  const secs = Math.max(1, Math.round((message.latency_ms ?? 0) / 1000));
  const steps = uniqueSteps(
    message.steps.map((st, i) => ({
      key: String(i),
      label: st.label,
      detail: st.detail,
      state: st.ok ? ('done' as const) : ('error' as const),
    })),
  );
  return (
    <Box>
      <TraceToggle open={open} onToggle={() => setOpen((o) => !o)}>
        Thought for {secs}s
      </TraceToggle>
      <AnimatePresence initial={false}>
        {open && (
          <MotionBox
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: EASE_OUT }}
            sx={{ overflow: 'hidden', pt: 1 }}
          >
            <StepList steps={steps} />
          </MotionBox>
        )}
      </AnimatePresence>
    </Box>
  );
}

function StatusNote({ status }: { status: ChatMessage['status'] }) {
  if (status !== 'unverified' && status !== 'error') return null;
  const unverified = status === 'unverified';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        mb: 1,
        fontSize: FS.label,
        fontWeight: 500,
        color: unverified ? '#b45309' : '#b91c1c',
      }}
    >
      {unverified ? (
        <ShieldOutlinedIcon sx={{ fontSize: 14 }} />
      ) : (
        <ErrorOutlineIcon sx={{ fontSize: 14 }} />
      )}
      {unverified ? "Answer held back: the numbers couldn't be verified" : 'Service unavailable'}
    </Box>
  );
}

const FEEDBACK_REASONS: { value: ChatFeedbackReason; label: string }[] = [
  { value: 'wrong_numbers', label: 'Numbers Look Wrong' },
  { value: 'not_what_i_asked', label: "Didn't Answer My Question" },
  { value: 'missing_data', label: 'Missing Data' },
  { value: 'hard_to_read', label: 'Hard to Read' },
  { value: 'too_slow', label: 'Too Slow' },
  { value: 'other', label: 'Other' },
];

/**
 * Thumbs-down follow-up, ChatGPT-style: pick what went wrong, optionally say
 * more. Saved with the answer (chat.messages.feedback_*) for review later.
 */
function FeedbackForm({
  initialReasons,
  initialComment,
  onCancel,
  onSubmit,
}: {
  initialReasons: ChatFeedbackReason[];
  initialComment: string;
  onCancel: () => void;
  onSubmit: (reasons: ChatFeedbackReason[], comment: string) => Promise<void>;
}) {
  const [reasons, setReasons] = useState<ChatFeedbackReason[]>(initialReasons);
  const [comment, setComment] = useState(initialComment);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const canSend = (reasons.length > 0 || comment.trim().length > 0) && !saving;
  const formRef = useRef<HTMLDivElement>(null);
  const toggle = (r: ChatFeedbackReason) =>
    setReasons((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  return (
    <MotionBox
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.22, ease: EASE_OUT }}
      // Once open, bring the whole form into view — it often opens below the fold.
      onAnimationComplete={() =>
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
      sx={{ overflow: 'hidden' }}
    >
      <Box
        ref={formRef}
        sx={{
          mt: 0.5,
          p: 1.5,
          scrollMarginBottom: 16,
          borderRadius: '12px',
          bgcolor: PANEL,
          border: `1px solid ${LINE}`,
          boxShadow: '0 4px 16px rgba(24, 24, 27, 0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ flex: 1, fontSize: FS.sm, fontWeight: 600, color: INK }}>
            What went wrong?
          </Typography>
          <IconButton
            size="small"
            aria-label="Close feedback"
            onClick={onCancel}
            sx={{ width: 24, height: 24, color: SUBTLE, '&:hover': { color: INK } }}
          >
            <CloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {FEEDBACK_REASONS.map(({ value, label }) => {
            const on = reasons.includes(value);
            return (
              <Box
                key={value}
                component="button"
                type="button"
                aria-pressed={on}
                onClick={() => toggle(value)}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  height: 28,
                  px: 1.25,
                  borderRadius: 999,
                  fontSize: FS.label,
                  fontWeight: 500,
                  border: `1px solid ${on ? 'rgba(124, 32, 244, 0.45)' : LINE_STRONG}`,
                  bgcolor: on ? ACCENT_TINT : PANEL,
                  color: on ? ACCENT_DEEP : TEXT,
                  transition: 'all 120ms ease',
                  '&:hover': {
                    borderColor: on ? 'rgba(124, 32, 244, 0.6)' : 'rgba(24, 24, 27, 0.3)',
                  },
                  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 1 },
                }}
              >
                {on && <CheckIcon sx={{ fontSize: 13 }} />}
                {label}
              </Box>
            );
          })}
        </Box>
        <InputBase
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, 1000))}
          placeholder="Tell us more (optional) — what did you expect?"
          multiline
          minRows={2}
          maxRows={5}
          inputProps={{ 'aria-label': 'Feedback details' }}
          sx={{
            mt: 1.25,
            width: '100%',
            px: 1.25,
            py: 1,
            borderRadius: '10px',
            bgcolor: SURFACE,
            border: '1px solid transparent',
            fontSize: FS.sm,
            color: TEXT,
            '&.Mui-focused': { bgcolor: PANEL, borderColor: 'rgba(124, 32, 244, 0.45)' },
            '& textarea::placeholder': { color: SUBTLE, opacity: 1 },
          }}
        />
        <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ flex: 1, fontSize: FS.micro, color: failed ? '#b91c1c' : SUBTLE }}>
            {failed ? "Couldn't send — please try again." : 'Helps us improve future answers.'}
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={onCancel}
            sx={{
              all: 'unset',
              cursor: 'pointer',
              height: 30,
              px: 1.25,
              borderRadius: 999,
              fontSize: FS.label,
              fontWeight: 500,
              color: MUTED,
              '&:hover': { color: INK, bgcolor: SURFACE },
            }}
          >
            Skip
          </Box>
          <Box
            component="button"
            type="button"
            disabled={!canSend}
            onClick={() => {
              setSaving(true);
              setFailed(false);
              onSubmit(reasons, comment.trim()).catch(() => {
                setFailed(true);
                setSaving(false);
              });
            }}
            sx={{
              all: 'unset',
              cursor: canSend ? 'pointer' : 'default',
              display: 'inline-flex',
              alignItems: 'center',
              height: 30,
              px: 1.5,
              borderRadius: 999,
              fontSize: FS.label,
              fontWeight: 600,
              color: canSend ? '#fff' : SUBTLE,
              bgcolor: canSend ? INK : SURFACE,
              transition: 'background-color 120ms ease',
              '&:hover': canSend ? { bgcolor: '#3f3f46' } : undefined,
              '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
            }}
          >
            {saving ? 'Sending…' : 'Send feedback'}
          </Box>
        </Box>
      </Box>
    </MotionBox>
  );
}

function ActionButton({
  title,
  onClick,
  children,
  pressed,
}: {
  title: string;
  onClick: () => void;
  children: ReactNode;
  /** Toggle state for rating buttons (aria-pressed). */
  pressed?: boolean;
}) {
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        onClick={onClick}
        aria-label={title}
        aria-pressed={pressed}
        sx={{
          width: 28,
          height: 28,
          borderRadius: '8px',
          color: pressed ? INK : SUBTLE,
          bgcolor: pressed ? SURFACE : 'transparent',
          '&:hover': { color: INK, bgcolor: SURFACE },
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
}

function AssistantMessage({
  message,
  onCite,
}: {
  message: ChatMessage;
  onCite: (source: ChatCitation, n: number) => void;
}) {
  const [rating, setRating] = useState<number | null>(message.feedback_rating);
  const [copied, setCopied] = useState(false);
  const citeNumbers = useMemo(
    () => new Map(message.citations.map((c, i) => [c.id, i + 1])),
    [message.citations],
  );
  const byId = useMemo(() => new Map(message.citations.map((c) => [c.id, c])), [message.citations]);
  const verified = message.status === 'answered' && message.citations.length > 0;

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // One short confirmation beside the action buttons ("Copied", "Thanks…").
  const [notice, setNotice] = useState<{ text: string; ok: boolean } | null>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(noticeTimer.current), []);
  const flash = (text: string, ok = true) => {
    setNotice({ text, ok });
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 2200);
  };
  const sayThanks = () => flash('Thanks for the feedback');
  const copy = () => {
    navigator.clipboard
      .writeText(stripCitations(message.content))
      .then(() => {
        setCopied(true);
        flash('Copied to clipboard');
        setTimeout(() => setCopied(false), 2200);
      })
      .catch(() => flash("Couldn't copy — select the text instead", false));
  };

  // Clicking the selected thumb again takes the rating back (ChatGPT / Gemini behaviour).
  const rate = (r: 1 | -1) => {
    const prev = rating;
    const next = rating === r ? null : r;
    setRating(next);
    setFeedbackOpen(next === -1);
    if (next === 1) sayThanks();
    else setNotice(null);
    void sendChatFeedback(message.id, { rating: next }).catch(() => setRating(prev));
  };

  return (
    <MotionBox
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      data-msg={message.id}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <AssistantHeader />
      <ThoughtToggle message={message} />
      <StatusNote status={message.status} />
      <ChatMarkdown
        text={message.content}
        citeNumbers={citeNumbers}
        sources={byId}
        onCite={(id) => {
          const s = byId.get(id);
          if (s) onCite(s, citeNumbers.get(id) ?? 0);
        }}
      />

      {message.citations.length > 0 && (
        <Box component="section" aria-label="Sources" sx={{ mt: 1.5 }}>
          <Typography
            sx={{ ...eyebrow, mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}
          >
            {message.status === 'unverified' ? 'Data consulted' : 'Sources'}
            <Box
              component="span"
              sx={{
                px: 0.6,
                height: 16,
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: 999,
                bgcolor: SURFACE,
                color: MUTED,
                letterSpacing: 0,
              }}
            >
              {message.citations.length}
            </Box>
          </Typography>
          <Box
            component="ol"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 230px), 1fr))',
              gap: 0.75,
            }}
          >
            {message.citations.map((c, i) => {
              const SourceIcon = c.kind === 'note' ? EventNoteOutlinedIcon : TableChartOutlinedIcon;
              return (
                <Box component="li" key={c.id} sx={{ minWidth: 0 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onCite(c, i + 1)}
                    aria-label={`Source ${i + 1}: ${c.title}. ${c.detail}`}
                    sx={{
                      all: 'unset',
                      boxSizing: 'border-box',
                      width: '100%',
                      height: '100%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      p: 1,
                      pr: 1.25,
                      borderRadius: '12px',
                      bgcolor: PANEL,
                      border: `1px solid ${LINE}`,
                      transition:
                        'border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease',
                      '&:hover, &:focus-visible': {
                        borderColor: 'rgba(124, 32, 244, 0.3)',
                        boxShadow: '0 4px 14px rgba(124, 32, 244, 0.08)',
                        transform: 'translateY(-1px)',
                      },
                      '&:hover .src-go, &:focus-visible .src-go': {
                        color: ACCENT,
                        transform: 'translate(1px, -1px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: 32,
                        height: 32,
                        borderRadius: '9px',
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                        background: `linear-gradient(135deg, ${ACCENT_TINT} 0%, #ede4fd 100%)`,
                        color: ACCENT_DEEP,
                      }}
                    >
                      <SourceIcon sx={{ fontSize: 16 }} />
                      <Box
                        component="span"
                        sx={{
                          position: 'absolute',
                          top: -5,
                          right: -5,
                          minWidth: 15,
                          height: 15,
                          px: '3px',
                          borderRadius: 999,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: PANEL,
                          boxShadow: `0 0 0 1px ${LINE_STRONG}`,
                          color: MUTED,
                          fontSize: '0.625rem',
                          fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {i + 1}
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        noWrap
                        sx={{ fontSize: FS.table, fontWeight: 600, color: INK, lineHeight: 1.35 }}
                      >
                        {c.title}
                      </Typography>
                      <Typography
                        noWrap
                        title={c.detail}
                        sx={{ mt: 0.25, fontSize: FS.label, color: MUTED, lineHeight: 1.35 }}
                      >
                        {c.detail}
                      </Typography>
                    </Box>
                    <NorthEastIcon
                      className="src-go"
                      sx={{
                        fontSize: 14,
                        color: SUBTLE,
                        flexShrink: 0,
                        transition: 'transform 150ms ease, color 150ms ease',
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25, ml: -0.5 }}>
        <ActionButton title={copied ? 'Copied' : 'Copy answer'} onClick={copy}>
          {copied ? (
            <CheckIcon sx={{ fontSize: 15, color: '#047857' }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 15 }} />
          )}
        </ActionButton>
        <ActionButton
          title={rating === 1 ? 'Remove rating' : 'Good response'}
          onClick={() => rate(1)}
          pressed={rating === 1}
        >
          {rating === 1 ? (
            <ThumbUpAltIcon sx={{ fontSize: 15 }} />
          ) : (
            <ThumbUpAltOutlinedIcon sx={{ fontSize: 15 }} />
          )}
        </ActionButton>
        <ActionButton
          title={rating === -1 ? 'Remove rating' : 'Bad response'}
          onClick={() => rate(-1)}
          pressed={rating === -1}
        >
          {rating === -1 ? (
            <ThumbDownAltIcon sx={{ fontSize: 15 }} />
          ) : (
            <ThumbDownAltOutlinedIcon sx={{ fontSize: 15 }} />
          )}
        </ActionButton>
        <AnimatePresence>
          {notice && (
            <MotionBox
              key={notice.text}
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              role="status"
              sx={{
                ml: 0.75,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                height: 22,
                px: 0.9,
                borderRadius: 999,
                fontSize: FS.label,
                fontWeight: 500,
                color: notice.ok ? '#047857' : '#b91c1c',
                bgcolor: notice.ok ? '#ecfdf5' : '#fef2f2',
              }}
            >
              {notice.ok ? (
                <CheckIcon sx={{ fontSize: 13 }} />
              ) : (
                <ErrorOutlineIcon sx={{ fontSize: 13 }} />
              )}
              {notice.text}
            </MotionBox>
          )}
        </AnimatePresence>
        {verified && (
          <Tooltip title="Every figure in this answer matched the source data">
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                ml: 'auto',
                height: 22,
                px: 0.9,
                borderRadius: 999,
                fontSize: FS.micro,
                fontWeight: 600,
                color: '#047857',
                bgcolor: '#ecfdf5',
              }}
            >
              <VerifiedIcon sx={{ fontSize: 13 }} />
              Verified
            </Box>
          </Tooltip>
        )}
      </Box>
      <AnimatePresence initial={false}>
        {feedbackOpen && (
          <FeedbackForm
            initialReasons={(message.feedback_reasons ?? []) as ChatFeedbackReason[]}
            initialComment={message.feedback_comment ?? ''}
            onCancel={() => setFeedbackOpen(false)}
            onSubmit={async (reasons, comment) => {
              await sendChatFeedback(message.id, { rating: -1, reasons, comment });
              setFeedbackOpen(false);
              sayThanks();
            }}
          />
        )}
      </AnimatePresence>
    </MotionBox>
  );
}

/** Suggested next questions under the latest answer; one click asks it. */
function FollowUps({
  message,
  onPick,
}: {
  message: ChatMessage | undefined;
  onPick: (q: string) => void;
}) {
  const items = message?.role === 'assistant' ? (message.follow_ups ?? []) : [];
  if (!items.length) return null;
  return (
    <MotionBox
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.15 }}
      sx={{ mt: -1 }}
    >
      <Typography sx={{ ...eyebrow, mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <AutoAwesomeOutlinedIcon sx={{ fontSize: 13, color: ACCENT }} />
        Ask next
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {items.map((q, i) => (
          <MotionBox
            key={q}
            role="button"
            tabIndex={0}
            onClick={() => onPick(q)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onPick(q);
              }
            }}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, delay: 0.2 + i * 0.06, ease: EASE_OUT }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              minHeight: 40,
              px: 1.5,
              py: 0.9,
              borderRadius: '12px',
              cursor: 'pointer',
              bgcolor: PANEL,
              border: `1px solid ${LINE}`,
              fontSize: FS.sm,
              lineHeight: 1.45,
              color: TEXT,
              transition:
                'border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease, color 150ms ease',
              '& .fu-go': {
                color: SUBTLE,
                transition: 'transform 150ms ease, color 150ms ease',
              },
              '&:hover, &:focus-visible': {
                color: INK,
                bgcolor: '#fcfaff',
                borderColor: 'rgba(124, 32, 244, 0.3)',
                boxShadow: '0 2px 10px rgba(124, 32, 244, 0.08)',
                outline: 'none',
                '& .fu-go': { color: ACCENT, transform: 'translateX(3px)' },
              },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>{q}</Box>
            <ArrowForwardIcon className="fu-go" sx={{ fontSize: 16, flexShrink: 0 }} />
          </MotionBox>
        ))}
      </Box>
    </MotionBox>
  );
}

/** Date bucket for a thread, ChatGPT-style. */
function historyGroup(iso: string, now: Date): string {
  const d = new Date(iso);
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return 'Previous 7 days';
  if (days < 30) return 'Previous 30 days';
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/** Time for a row: "2:14 PM" today, "Mon" this week, "Oct 3" otherwise. */
function historyTime(iso: string, group: string): string {
  const d = new Date(iso);
  if (group === 'Today' || group === 'Yesterday') {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  if (group === 'Previous 7 days') return d.toLocaleDateString(undefined, { weekday: 'short' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * The signed-in person's own threads (the API filters by user — nobody else's
 * history, admins included), grouped by date, searchable, with a one-line
 * preview of the latest answer.
 */
function HistoryList({
  activeId,
  onPick,
  onDeleted,
  onNew,
  onBack,
}: {
  activeId: number | null;
  onPick: (id: number) => void;
  onDeleted: (id: number) => void;
  onNew: () => void;
  onBack: () => void;
}) {
  const conversations = useChatConversations(true);
  const archive = useArchiveConversation();
  const [query, setQuery] = useState('');
  const rows = conversations.data ?? [];
  const q = query.trim().toLowerCase();
  const filtered = q
    ? rows.filter((c) => `${c.title ?? ''} ${c.preview ?? ''}`.toLowerCase().includes(q))
    : rows;
  const now = new Date();
  const groups: { name: string; items: typeof rows }[] = [];
  for (const c of filtered) {
    const name = historyGroup(c.updated_at, now);
    const last = groups[groups.length - 1];
    if (last && last.name === name) last.items.push(c);
    else groups.push({ name, items: [c] });
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
        <Box
          component="button"
          type="button"
          onClick={onBack}
          sx={{
            all: 'unset',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            ml: -0.5,
            mb: 1,
            fontSize: FS.label,
            color: MUTED,
            '&:hover, &:focus-visible': { color: INK },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 17 }} />
          Back to chat
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography
            sx={{ fontSize: '1.125rem', fontWeight: 650, letterSpacing: '-0.015em', color: INK }}
          >
            History
          </Typography>
          {rows.length > 0 && (
            <Box
              sx={{
                px: 0.75,
                height: 20,
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: 999,
                bgcolor: SURFACE,
                fontSize: FS.micro,
                fontWeight: 600,
                color: MUTED,
              }}
            >
              {rows.length}
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          <Box
            component="button"
            type="button"
            onClick={onNew}
            sx={{
              all: 'unset',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              height: 30,
              px: 1.25,
              borderRadius: 999,
              fontSize: FS.label,
              fontWeight: 600,
              color: '#fff',
              bgcolor: INK,
              '&:hover': { bgcolor: '#3f3f46' },
              '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
            }}
          >
            <AddIcon sx={{ fontSize: 15 }} />
            New chat
          </Box>
        </Box>
        {rows.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: 38,
              px: 1.5,
              borderRadius: 999,
              bgcolor: SURFACE,
              border: '1px solid transparent',
              transition:
                'background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease',
              '&:focus-within': {
                bgcolor: PANEL,
                borderColor: 'rgba(124, 32, 244, 0.45)',
                boxShadow: `0 0 0 3px ${ACCENT_SOFT}`,
              },
            }}
          >
            <SearchIcon sx={{ fontSize: 17, color: SUBTLE }} />
            <InputBase
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search conversations"
              inputProps={{ 'aria-label': 'Search conversations' }}
              sx={{
                flex: 1,
                fontSize: FS.sm,
                color: INK,
                '& input::placeholder': { color: SUBTLE, opacity: 1 },
              }}
            />
            {query && (
              <IconButton
                size="small"
                aria-label="Clear search"
                onClick={() => setQuery('')}
                sx={{ p: 0.25, color: SUBTLE }}
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.25, pb: 1.5 }}>
        {conversations.isLoading &&
          [0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{ px: 1.25, py: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}
            >
              <MotionBox
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.1 }}
                sx={{ height: 10, width: '60%', borderRadius: 1, bgcolor: SURFACE }}
              />
              <MotionBox
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.1 + 0.05 }}
                sx={{ height: 8, width: '85%', borderRadius: 1, bgcolor: SURFACE }}
              />
            </Box>
          ))}

        {conversations.isError && (
          <Box sx={{ textAlign: 'center', px: 3, py: 6 }}>
            <Typography sx={{ fontSize: FS.sm, fontWeight: 600, color: INK }}>
              Couldn't load your conversations
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={() => void conversations.refetch()}
              sx={{
                all: 'unset',
                cursor: 'pointer',
                mt: 1,
                fontSize: FS.label,
                fontWeight: 600,
                color: LINK,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Try again
            </Box>
          </Box>
        )}

        {!conversations.isLoading && !conversations.isError && rows.length === 0 && (
          <Box sx={{ textAlign: 'center', px: 3, py: 6 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                mx: 'auto',
                mb: 1.5,
                borderRadius: '12px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: SURFACE,
                color: MUTED,
              }}
            >
              <HistoryIcon sx={{ fontSize: 22 }} />
            </Box>
            <Typography sx={{ fontSize: FS.sm, fontWeight: 600, color: INK }}>
              No conversations yet
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: FS.label, color: MUTED }}>
              Questions you ask appear here.
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={onNew}
              sx={{
                all: 'unset',
                mt: 2,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                height: 32,
                borderRadius: 999,
                cursor: 'pointer',
                fontSize: FS.label,
                fontWeight: 600,
                color: '#fff',
                bgcolor: INK,
                '&:hover': { bgcolor: '#3f3f46' },
                '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
              }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              Ask a question
            </Box>
          </Box>
        )}

        {!conversations.isLoading && rows.length > 0 && filtered.length === 0 && (
          <Typography sx={{ px: 1.25, py: 3, fontSize: FS.sm, color: MUTED, textAlign: 'center' }}>
            No conversations match “{query.trim()}”.
          </Typography>
        )}

        {groups.map((g) => (
          <Box key={g.name} component="section" sx={{ mb: 1 }}>
            <Typography
              sx={{
                ...eyebrow,
                position: 'sticky',
                top: 0,
                zIndex: 1,
                px: 1.25,
                py: 0.75,
                bgcolor: PANEL_TINT,
              }}
            >
              {g.name}
            </Typography>
            {g.items.map((c, idx) => {
              const active = c.id === activeId;
              return (
                <MotionBox
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onPick(c.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onPick(c.id);
                    }
                  }}
                  initial={{ opacity: 0, y: 3 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: Math.min(idx, 8) * 0.025 }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.25,
                    py: 0.9,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    bgcolor: active ? ACCENT_TINT : 'transparent',
                    transition: 'background-color 120ms ease',
                    '&:hover, &:focus-visible': {
                      bgcolor: active ? ACCENT_TINT : SURFACE,
                      outline: 'none',
                    },
                    '&:hover .hy-del, &:focus-within .hy-del': { opacity: 1 },
                    '&:hover .hy-time, &:focus-within .hy-time': { opacity: { sm: 0 } },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: FS.sm,
                        fontWeight: 500,
                        color: active ? ACCENT_DEEP : INK,
                        lineHeight: 1.4,
                      }}
                    >
                      {c.title ?? 'Untitled conversation'}
                    </Typography>
                    {c.preview && (
                      <Typography
                        noWrap
                        sx={{ fontSize: FS.label, color: SUBTLE, lineHeight: 1.45 }}
                      >
                        {c.question_count > 1 && (
                          <Box component="span" sx={{ color: MUTED, fontWeight: 500 }}>
                            {c.question_count} questions ·{' '}
                          </Box>
                        )}
                        {c.preview}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{ position: 'relative', flexShrink: 0, minWidth: 32, textAlign: 'right' }}
                  >
                    <Typography
                      className="hy-time"
                      sx={{
                        fontSize: FS.micro,
                        color: SUBTLE,
                        fontVariantNumeric: 'tabular-nums',
                        transition: 'opacity 120ms',
                      }}
                    >
                      {historyTime(c.updated_at, g.name)}
                    </Typography>
                    <Tooltip title="Delete">
                      <IconButton
                        className="hy-del"
                        size="small"
                        aria-label="Delete conversation"
                        onClick={(e) => {
                          e.stopPropagation();
                          archive.mutate(c.id, { onSuccess: () => onDeleted(c.id) });
                        }}
                        sx={{
                          position: { xs: 'static', sm: 'absolute' },
                          top: '50%',
                          right: -4,
                          translate: { sm: '0 -50%' },
                          mt: { xs: 0.25, sm: 0 },
                          width: 26,
                          height: 26,
                          opacity: { xs: 1, sm: 0 },
                          color: SUBTLE,
                          transition: 'opacity 120ms',
                          '&:hover': { color: '#b91c1c', bgcolor: '#fef2f2' },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </MotionBox>
              );
            })}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1.25,
          borderTop: `1px solid ${LINE}`,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          fontSize: FS.micro,
          color: SUBTLE,
        }}
      >
        <InsightsOutlinedIcon sx={{ fontSize: 13 }} />
        Questions are reviewed to help improve future answers.
      </Box>
    </Box>
  );
}
